import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SparkleEffect } from '../../components/SparkleEffect';
import { GlitterHeart } from '../../components/GlitterHeart';
import { CalendarIcon, RepeatIcon, RefreshCwIcon, SparklesIcon, SmileIcon, MessageCircleHeartIcon, ZapIcon, CheckSquareIcon, DumbbellIcon, HomeIcon, PaletteIcon, ShieldCheckIcon, ArrowRightIcon, ClockIcon } from 'lucide-react';
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.base69e03f5b54ed4865741c1bde.app';
export function TrippinThroughLifePage() {
  const features = [{
    icon: <CalendarIcon className="w-6 h-6" data-id="element-837" />,
    title: 'AI-powered personalized schedules'
  }, {
    icon: <RepeatIcon className="w-6 h-6" data-id="element-838" />,
    title: 'Adaptive routines and recurring chore systems'
  }, {
    icon: <RefreshCwIcon className="w-6 h-6" data-id="element-839" />,
    title: 'Flexible and rotating schedule support'
  }, {
    icon: <SparklesIcon className="w-6 h-6" data-id="element-840" />,
    title: 'Deep cleaning checklists and home reset plans'
  }, {
    icon: <SmileIcon className="w-6 h-6" data-id="element-841" />,
    title: 'Mood check-ins and wellness support'
  }, {
    icon: <MessageCircleHeartIcon className="w-6 h-6" data-id="element-842" />,
    title: 'Daily affirmations and motivation'
  }, {
    icon: <ZapIcon className="w-6 h-6" data-id="element-843" />,
    title: 'Personalized "Tiny Badass Actions"'
  }, {
    icon: <CheckSquareIcon className="w-6 h-6" data-id="element-844" />,
    title: 'AI-generated custom checklists'
  }, {
    icon: <DumbbellIcon className="w-6 h-6" data-id="element-845" />,
    title: 'Fitness and self-care routine tracking'
  }, {
    icon: <HomeIcon className="w-6 h-6" data-id="element-846" />,
    title: 'Family and household task organization'
  }, {
    icon: <PaletteIcon className="w-6 h-6" data-id="element-847" />,
    title: 'Customizable themes and personalized app experience'
  }];
  return <div className="min-h-screen bg-gradient-to-b from-blush-50 via-white to-peach-50 pt-28 pb-24" data-id="element-848">
      <SparkleEffect count={80} data-id="element-849" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-850">
        {/* Hero Section */}
        <div className="text-center mb-16" data-id="element-851">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="w-32 h-32 md:w-48 md:h-48 mx-auto mb-8 rounded-3xl bg-white shadow-xl shadow-blush-200/50 flex items-center justify-center p-4 border border-blush-100" data-id="element-852">
            <img src="https://cdn.magicpatterns.com/uploads/gieTJfs2YUH6aZyKV1oyEk/ChatGPT_Image_May_11,_2026,_01_53_32_PM.png" alt="Trippin' Through Life App Logo" className="w-full h-full object-contain" data-id="element-853" />
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="font-heading text-4xl md:text-6xl font-bold text-plum-800 mb-6" data-id="element-854">
            Trippin' Through Life
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-xl text-plum-600 font-medium max-w-3xl mx-auto leading-relaxed" data-id="element-855">
            Your AI-powered life, home, and wellness management system designed
            to reduce overwhelm, build routines, and help you stay on top of
            everyday life — without burnout.
          </motion.p>
        </div>

        {/* Description */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-blush-100 mb-16" data-id="element-856">
          <p className="text-plum-700 text-lg leading-relaxed mb-6" data-id="element-857">
            Using intelligent conversational onboarding, TTL learns your unique
            lifestyle, household structure, schedules, routines, stress levels,
            cleaning needs, and personal goals to create fully personalized
            daily plans, chore systems, reset routines, wellness check-ins, and
            smart checklists tailored specifically to you.
          </p>
          <p className="text-plum-700 text-lg leading-relaxed font-medium" data-id="element-858">
            Whether you're managing a busy family, flexible work schedule,
            content creation, school routines, mental overload, fitness goals,
            or simply trying to get your life back on track, TTL adapts to your
            real life — not the other way around.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="mb-20" data-id="element-859">
          <h2 className="font-heading text-3xl font-bold text-center text-plum-800 mb-10" data-id="element-860">
            Everything you need to thrive
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-861">
            {features.map((feature, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.05
          }} className="flex items-center gap-4 bg-white/60 backdrop-blur-sm p-5 rounded-2xl border border-blush-200/50 shadow-sm hover:bg-white transition-colors" data-id="element-862">
                <div className="w-12 h-12 rounded-xl bg-blush-100 flex items-center justify-center text-cta-500 shrink-0" data-id="element-863">
                  {feature.icon}
                </div>
                <span className="font-bold text-plum-700" data-id="element-864">{feature.title}</span>
              </motion.div>)}
          </div>
        </div>

        {/* Tagline & Waitlist */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center bg-gradient-to-br from-plum-900 to-plum-800 rounded-[3rem] p-10 md:p-16 shadow-2xl relative overflow-hidden mb-20" data-id="element-865">
          <div className="absolute top-0 right-0 w-64 h-64 bg-cta-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" data-id="element-866" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-blush-500/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3" data-id="element-867" />

          <div className="relative z-10" data-id="element-868">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/20 text-emerald-300 text-sm font-bold uppercase tracking-wider border border-emerald-400/30 mb-6" data-id="element-869">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" data-id="element-870" />
              Now Live on Google Play
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-871">
              From survival mode to thriving mode.
            </h2>
            <p className="text-plum-200 text-lg mb-10 max-w-xl mx-auto" data-id="element-872">
              Trippin' Through Life is live on Google Play — download it now and
              start turning chaos into structure, one manageable step at a time.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-xl mx-auto" data-id="element-873">
              {/* Google Play — Live */}
              <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-7 py-4 bg-white text-plum-900 rounded-2xl font-bold hover:bg-blush-50 transition-colors shadow-lg shadow-black/20" data-id="element-874">
                <svg className="w-6 h-6" viewBox="0 0 512 512" aria-hidden="true" data-id="element-875">
                  <path fill="#00d2ff" d="M48 59.5v393l205-196.5z" data-id="element-876" />
                  <path fill="#00f076" d="M48 59.5l257 137-58 59z" data-id="element-877" />
                  <path fill="#ffce00" d="M48 452.5l199-196 58 59z" data-id="element-878" />
                  <path fill="#ff3a44" d="M305 196.5l60 32c30 16 30 39 0 55l-60 32-58-59z" data-id="element-879" />
                </svg>
                <span className="text-left leading-tight" data-id="element-880">
                  <span className="block text-[10px] font-semibold uppercase tracking-wider text-plum-500" data-id="element-881">
                    Get it on
                  </span>
                  Google Play
                </span>
              </a>

              {/* Apple App Store — Coming Soon */}
              <div className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-7 py-4 bg-white/10 text-plum-200 rounded-2xl font-bold border border-white/20 cursor-default select-none" aria-label="Coming soon to the Apple App Store" data-id="element-882">
                <svg className="w-6 h-6" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true" data-id="element-883">
                  <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" data-id="element-884" />
                </svg>
                <span className="text-left leading-tight" data-id="element-885">
                  <span className="block text-[10px] font-semibold uppercase tracking-wider text-plum-300/80" data-id="element-886">
                    Coming soon to
                  </span>
                  App Store
                </span>
                <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 text-[10px] font-bold uppercase tracking-wider" data-id="element-887">
                  <ClockIcon className="w-3 h-3" data-id="element-888" />
                  Soon
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Privacy Policy Link Card */}
        <Link to="/apps/trippin-through-life/privacy" className="group block bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-blush-200 hover:shadow-lg hover:border-blush-300 transition-all" data-id="element-889">
          <div className="flex items-center gap-5" data-id="element-890">
            <div className="w-14 h-14 rounded-2xl bg-blush-100 flex items-center justify-center shrink-0 group-hover:bg-blush-200 transition-colors" data-id="element-891">
              <ShieldCheckIcon className="w-7 h-7 text-cta-500" data-id="element-892" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-893">
              <h3 className="font-heading text-xl font-bold text-plum-800 mb-1" data-id="element-894">
                Privacy Policy
              </h3>
              <p className="text-plum-500 text-sm font-medium" data-id="element-895">
                How we handle your data — effective May 12, 2026
              </p>
            </div>
            <ArrowRightIcon className="w-5 h-5 text-plum-400 group-hover:text-cta-500 group-hover:translate-x-1 transition-all shrink-0" data-id="element-896" />
          </div>
        </Link>
      </div>
    </div>;
}